import React, {Fragment} from 'react';
import {useWpContext} from "../context/WordPressProvider.tsx";
import {useFetchRoomsQuery} from "../../store/api-rest.ts";
import {
    CtrDomainsVf,
    CtrDynamicTables,
    CtrInscriptions,
    CtrPiecesUnites,
    CtrVfCouvrePlancherCode,
    CtrVfNiveau,
    CtrVfPieceCode
} from "../../graphql/generated/graphql-main.tsx";
import useCtrValeursFixes from "../../hooks/useCtrValeursFixes.ts";
import {Divider} from "@mui/material";
import useCtrMetaFields from "../../hooks/useCtrMetaFields.tsx";
import {useSingleListing} from "../context/SingleListingProvider.tsx";
import {strEmpty} from "../../utils/utils.ts";

const ListingTabRooms = () => {
    const {others, is_fr} = useWpContext<ZListingSingle>();
    const {fixedValue} = useCtrValeursFixes();
    const {toLabel, toHtml} = useCtrMetaFields();
    const {listing} = useSingleListing();
    const {data} = useFetchRoomsQuery(others.mls_id);

    const elem = toHtml<CtrInscriptions>(listing, CtrDynamicTables.Inscriptions);
    const lbl = (fld: keyof CtrPiecesUnites) =>
        toLabel<CtrPiecesUnites>(CtrDynamicTables.PiecesUnites, fld);

    // check for empty columns to remove them when no room has any of the specified details
    const hasFlooring = data?.some(it => !strEmpty(it.COUVRE_PLANCHER_CODE)) ?? false;
    const hasFireplace = data?.some(it => !strEmpty(it.IND_FOYER_POELE)) ?? false;
    const hasDetails = data?.some(it => !strEmpty(is_fr ? it.INFO_SUPP_F : it.INFO_SUPP_A)) ?? false;
    // number of final columns for grid display (3 basic ones always there + additional ones
    const tblColCount = 3 + [hasFlooring, hasFireplace, hasDetails].filter(it => it == true).length;

    return <section className={""}>

        {/* if no rooms in PIECES_UNITES table, then show total units count from INSCRIPTIONS table */}
        {data?.length! == 0 && (
            <dl className={"rooms__summary"}>
                <div>{elem("NB_UNITES_TOTAL") || elem("NB_PIECES")}</div>
                <div>{elem("NB_SALLES_BAINS")}</div>
                <div>{elem("NB_CHAMBRES")}</div>
            </dl>
        )}

        {/* grid-based display -- only render if at least 1 room */}
        {data?.length! > 0 && <Fragment>
            {/* columns */}
            <div className={`grid grid-cols-1 gap-0 md:grid-cols-${tblColCount} md:gap-4 *:font-bold *:break-words`}>
                <div className={"hidden md:block"}>{lbl("PIECE_CODE")}</div>
                <div className={"hidden md:block"}>{lbl("NIVEAU")}</div>
                <div className={"hidden md:block"}>{lbl("DIMENSIONS")}</div>
                {hasFlooring && <div className={"hidden md:block"}>{lbl("COUVRE_PLANCHER_CODE")}</div>}
                {hasFireplace && <div className={"hidden md:block"}>{lbl("IND_FOYER_POELE")}</div>}
                {hasDetails && <div className={"hidden md:block"}>{lbl(is_fr ? "INFO_SUPP_F" : "INFO_SUPP_A")}</div>}

                {/*<div className={"hidden md:block"}>{lbl("IND_IRREGULIER")}</div>*/}
                {/*<div>{lbl(is_fr ? "AU_PIECE_INFO_F" : "AU_PIECE_INFO_A")}</div>*/}
                {/*<div>{lbl(is_fr ? "AU_NIVEAU_INFO_F" : "AU_NIVEAU_INFO_A")}</div>*/}
                {/*<div>{lbl(is_fr ? "AU_COUVRE_PLANCHER_F" : "AU_COUVRE_PLANCHER_A")}</div>*/}
            </div>
            <Divider className={"hidden md:block md:my-2 bg-crimson"}/>

            {/* grid rows */}
            <dl className={`mt-4 grid grid-cols-1 gap-0 md:grid-cols-${tblColCount} md:gap-4`}>
                {data?.map((it: CtrPiecesUnites, idx) => {
                    return <Fragment key={`room-${it.SEQ}`}>
                        <div className={"*:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>{lbl("PIECE_CODE")} {" : "}</dt>
                            <dd>{it?.PIECE_CODE == CtrVfPieceCode.Au
                                ? (is_fr ? it?.AU_PIECE_INFO_F : it?.AU_PIECE_INFO_A)
                                : fixedValue(CtrDomainsVf.PieceCode, it?.PIECE_CODE)}</dd>
                        </div>
                        <div className={"*:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>{lbl("NIVEAU")} {" : "}</dt>
                            <dd>{it?.NIVEAU == CtrVfNiveau.Au
                                ? (is_fr ? it?.AU_NIVEAU_INFO_F : it?.AU_NIVEAU_INFO_A)
                                : fixedValue(CtrDomainsVf.Niveau, it?.NIVEAU)}</dd>
                        </div>
                        <div className={"*:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>{lbl("DIMENSIONS")} {" : "}</dt>
                            <dd>{(it?.DIMENSIONS ?? "nd").toLowerCase()}</dd>
                        </div>
                        {hasFlooring && <div className={"*:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>{lbl("COUVRE_PLANCHER_CODE")} {" : "}</dt>
                            <dd>{it?.COUVRE_PLANCHER_CODE == CtrVfCouvrePlancherCode.Au
                                ? (is_fr ? it?.AU_COUVRE_PLANCHER_F : it?.AU_COUVRE_PLANCHER_A)
                                : fixedValue(CtrDomainsVf.CouvrePlancherCode, it?.COUVRE_PLANCHER_CODE)}</dd>
                        </div>}
                        {hasFireplace && <div className={"*:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>{lbl("IND_FOYER_POELE")} {" : "}</dt>
                            <dd>{it?.IND_FOYER_POELE == "O" ? '✔' : ''}</dd>
                        </div>}
                        {hasDetails && <div className={"mb-4 mg:mb-0 *:inline md:block"}>
                            <dt className={"font-bold md:hidden"}>
                                {lbl(is_fr ? "INFO_SUPP_F" : "INFO_SUPP_A")}{' : '}
                            </dt>
                            <dd>{is_fr ? it?.INFO_SUPP_F : it?.INFO_SUPP_A}</dd>
                        </div>}

                        {/*<div>*/}
                        {/*    <span className={"font-bold md:hidden"}>{lbl("IND_IRREGULIER")} : </span>*/}
                        {/*    <span>{it?.IND_IRREGULIER}</span>*/}
                        {/*</div>*/}
                        {/*<div>{is_fr ? it?.AU_PIECE_INFO_F : it?.AU_PIECE_INFO_A}</div>*/}
                        {/*<div>{is_fr ? it?.AU_NIVEAU_INFO_F : it?.AU_NIVEAU_INFO_A}</div>*/}
                        {/*<div>{is_fr ? it?.AU_COUVRE_PLANCHER_F : it?.AU_COUVRE_PLANCHER_A}</div>*/}
                    </Fragment>
                })}
            </dl>
        </Fragment>}
    </section>
};

export default ListingTabRooms;